import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { showSuccess, showError } from '../utils/alertas';

const IS_PROD = true;
const API_BASE_URL = IS_PROD ? 'https://sigoapi.jhe-etes.com.br/api' : 'http://localhost:3001/api';

// ============================================================
// COMPONENTE: Badge de status da linha do histórico
// ============================================================
const StatusBadge = ({ atualizados, ignorados }) => {
    if (ignorados === 0) return (
        <span className="text-[10px] font-bold bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">
            ✅ Completo
        </span>
    );
    if (atualizados === 0) return (
        <span className="text-[10px] font-bold bg-red-100 text-red-600 px-2 py-0.5 rounded-full">
            ❌ Nenhuma aba
        </span>
    );
    return (
        <span className="text-[10px] font-bold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
            ⚠️ Parcial
        </span>
    );
};

// ============================================================
// COMPONENTE PRINCIPAL
// ============================================================
function AdminPanel() {
    const navigate = useNavigate();
    const fileInputRef = useRef(null);
    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user') || '{}');

    const [historico, setHistorico] = useState([]);
    const [loadingHistorico, setLoadingHistorico] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [progresso, setProgresso] = useState(0);
    const [resultado, setResultado] = useState(null); // resultado do último upload
    const [expandedRow, setExpandedRow] = useState(null);

    // ----------------------------------------------------------
    // CARREGAR HISTÓRICO DE IMPORTAÇÕES
    // ----------------------------------------------------------
    const fetchHistorico = useCallback(async () => {
        setLoadingHistorico(true);
        try {
            const res = await fetch(`${API_BASE_URL}/historico-importacoes`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.status === 403) {
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                navigate('/login');
                return;
            }
            if (res.ok) {
                const data = await res.json();
                setHistorico(data);
            } else {
                showError('Erro', 'Não foi possível carregar o histórico.');
            }
        } catch {
            showError('Conexão', 'Erro ao conectar com o servidor.');
        } finally {
            setLoadingHistorico(false);
        }
    }, [token, navigate]);

    useEffect(() => {
        if (!token) { navigate('/login'); return; }
        fetchHistorico();
    }, [fetchHistorico, token, navigate]);

    // ----------------------------------------------------------
    // UPLOAD E PROCESSAMENTO DO EXCEL GLOBAL
    // ----------------------------------------------------------
    const handleFileSelect = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        e.target.value = '';

        setUploading(true);
        setResultado(null);
        setProgresso(10);

        const formData = new FormData();
        formData.append('file', file);

        try {
            setProgresso(30);
            const res = await fetch(`${API_BASE_URL}/importar-metas-global`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });

            setProgresso(90);
            const data = await res.json();

            if (res.status === 403) {
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                navigate('/login');
                return;
            }

            if (res.ok) {
                setProgresso(100);
                setResultado(data);
                await showSuccess('Importação Concluída!', `${data.total_atualizados} ETEs atualizadas com sucesso.`);
                fetchHistorico();
            } else {
                showError('Erro na Importação', data.message || 'Verifique o arquivo e tente novamente.');
            }
        } catch (error) {
            console.error(error);
            showError('Erro de Resposta', `Não comunicou corretamente com a rota: ${error.message}`);
        } finally {
            setUploading(false);
            setTimeout(() => setProgresso(0), 1500);
        }
    };

    // ----------------------------------------------------------
    // HELPERS
    // ----------------------------------------------------------
    const formatDateTime = (iso) => {
        if (!iso) return '-';
        const d = new Date(iso);
        return d.toLocaleDateString('pt-BR') + ' às ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    };

    const parseDetalhes = (jsonStr) => {
        try { return JSON.parse(jsonStr); } catch { return []; }
    };

    // ============================================================
    // RENDER
    // ============================================================
    return (
        <div className="min-h-screen bg-[#F8F9FA] font-sans">

            {/* ── HEADER ────────────────────────────────────────── */}
            <div className="bg-white border-b border-gray-100 shadow-sm sticky top-0 z-20">
                <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => navigate('/')}
                            className="text-gray-400 hover:text-sabesp-blue text-sm font-bold flex items-center gap-1.5 transition-colors"
                        >
                            ← Voltar ao Mapa
                        </button>
                        <div className="w-px h-5 bg-gray-200" />
                        <div className="flex items-center gap-2">
                            <span className="text-xl">⚙️</span>
                            <div>
                                <h1 className="text-base font-black text-slate-800 leading-none">Painel Administrativo</h1>
                                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">Importação de Metas</p>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 bg-slate-50 border border-slate-100 rounded-xl px-3 py-2 self-end sm:self-auto">
                        <span className="w-7 h-7 bg-sabesp-blue text-white rounded-full flex items-center justify-center text-xs font-black">
                            {(user.nome || 'A')[0].toUpperCase()}
                        </span>
                        <div>
                            <p className="text-xs font-bold text-slate-700 leading-none">{user.nome || 'Administrador'}</p>
                            <p className="text-[9px] text-gray-400 font-bold uppercase">{user.tipo || 'admin'}</p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6 py-8 space-y-8">

                {/* ── SEÇÃO 1: UPLOAD GLOBAL ─────────────────────── */}
                <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
                    <div className="p-8 border-b border-gray-50">
                        <h2 className="text-lg font-black text-gray-800 flex items-center gap-2 mb-1">
                            <span>📊</span> Importar Planilha Geral
                        </h2>
                        <p className="text-xs text-gray-400 font-medium">
                            O arquivo Excel deve ter uma aba para cada ETE com o <strong>nome exato</strong> do empreendimento cadastrado no sistema.
                        </p>
                    </div>

                    <div className="p-8">
                        <input
                            type="file"
                            ref={fileInputRef}
                            accept=".xlsx,.xls"
                            onChange={handleFileSelect}
                            className="hidden"
                        />

                        {/* Drop/Click Area */}
                        <div
                            onClick={() => !uploading && fileInputRef.current.click()}
                            className={`relative border-2 border-dashed rounded-3xl p-14 flex flex-col items-center justify-center gap-4 transition-all cursor-pointer group
                                ${uploading
                                    ? 'border-sabesp-blue bg-blue-50/30 cursor-wait'
                                    : 'border-gray-200 hover:border-sabesp-blue hover:bg-blue-50/20'
                                }`}
                        >
                            <div className={`w-16 h-16 rounded-2xl flex items-center justify-center text-3xl transition-all shadow-sm
                                ${uploading ? 'bg-sabesp-blue/10 animate-pulse' : 'bg-gray-50 group-hover:bg-sabesp-blue/10 group-hover:scale-110'}`}>
                                {uploading ? '⏳' : '📋'}
                            </div>
                            <div className="text-center">
                                <p className="font-black text-gray-700 text-base">
                                    {uploading ? 'Processando planilha...' : 'Selecionar arquivo Excel'}
                                </p>
                                <p className="text-xs text-gray-400 mt-1">
                                    {uploading ? 'Aguarde, estamos atualizando todas as ETEs...' : 'Clique aqui ou arraste o arquivo .xlsx'}
                                </p>
                            </div>
                        </div>

                        {/* Barra de progresso */}
                        {uploading && (
                            <div className="mt-4 space-y-1">
                                <div className="flex justify-between text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                                    <span>Processando ETEs...</span>
                                    <span>{progresso}%</span>
                                </div>
                                <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-sabesp-blue transition-all duration-500 rounded-full"
                                        style={{ width: `${progresso}%` }}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Resultado do último upload */}
                        {resultado && !uploading && (
                            <div className="mt-6 bg-slate-50 rounded-2xl border border-slate-100 p-6 animate-fadeIn">
                                <h4 className="text-xs font-black text-gray-500 uppercase tracking-widest mb-4 flex items-center gap-2">
                                    <span>📋</span> Resultado da Importação
                                </h4>
                                <div className="grid grid-cols-3 gap-4 mb-4">
                                    <div className="text-center bg-white rounded-2xl p-4 border border-gray-100 shadow-sm">
                                        <p className="text-3xl font-black text-sabesp-blue">{resultado.total_etes || 0}</p>
                                        <p className="text-[10px] text-gray-400 font-bold uppercase mt-1">Total de ETEs</p>
                                    </div>
                                    <div className="text-center bg-emerald-50 rounded-2xl p-4 border border-emerald-100 shadow-sm">
                                        <p className="text-3xl font-black text-emerald-600">{resultado.total_atualizados || 0}</p>
                                        <p className="text-[10px] text-emerald-600 font-bold uppercase mt-1">Atualizadas ✅</p>
                                    </div>
                                    <div className="text-center bg-amber-50 rounded-2xl p-4 border border-amber-100 shadow-sm">
                                        <p className="text-3xl font-black text-amber-600">{resultado.total_ignorados || 0}</p>
                                        <p className="text-[10px] text-amber-600 font-bold uppercase mt-1">Não Encontradas</p>
                                    </div>
                                </div>

                                {resultado.ignorados && resultado.ignorados.length > 0 && (
                                    <div>
                                        <p className="text-[10px] font-bold text-amber-600 uppercase tracking-wider mb-2">ETEs não encontradas na planilha:</p>
                                        <div className="flex flex-wrap gap-2">
                                            {resultado.ignorados.map((nome, i) => (
                                                <span key={i} className="text-[10px] bg-amber-100 text-amber-700 px-2 py-1 rounded-lg font-bold border border-amber-200">
                                                    {nome}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* ── SEÇÃO 2: HISTÓRICO ─────────────────────────── */}
                <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
                    <div className="p-8 border-b border-gray-50 flex items-center justify-between">
                        <div>
                            <h2 className="text-lg font-black text-gray-800 flex items-center gap-2 mb-1">
                                <span>🕒</span> Histórico de Importações
                            </h2>
                            <p className="text-xs text-gray-400 font-medium">
                                Registro completo de todas as planilhas importadas por usuário
                            </p>
                        </div>
                        <button
                            onClick={fetchHistorico}
                            className="text-[10px] font-bold text-gray-400 hover:text-sabesp-blue uppercase tracking-widest flex items-center gap-1.5 transition-colors border border-gray-100 hover:border-blue-100 px-3 py-2 rounded-xl"
                        >
                            🔄 Atualizar
                        </button>
                    </div>

                    {loadingHistorico ? (
                        <div className="p-16 flex flex-col items-center gap-3 text-gray-400">
                            <span className="text-2xl animate-spin">⏳</span>
                            <p className="text-sm font-medium">Carregando histórico...</p>
                        </div>
                    ) : historico.length === 0 ? (
                        <div className="p-16 flex flex-col items-center gap-3 text-gray-400">
                            <span className="text-5xl opacity-20">📂</span>
                            <p className="text-sm font-medium">Nenhuma importação registrada ainda.</p>
                            <p className="text-xs opacity-60">Faça o primeiro upload de planilha acima.</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-left text-xs border-collapse">
                                <thead>
                                    <tr className="bg-slate-50 text-slate-500 font-black uppercase tracking-wider border-b border-gray-100">
                                        <th className="px-6 py-4">Data / Hora</th>
                                        <th className="px-4 py-4">Usuário</th>
                                        <th className="px-4 py-4">Arquivo</th>
                                        <th className="px-4 py-4 text-center">Total ETEs</th>
                                        <th className="px-4 py-4 text-center">Atualizadas</th>
                                        <th className="px-4 py-4 text-center">Ignoradas</th>
                                        <th className="px-4 py-4 text-center">Status</th>
                                        <th className="px-4 py-4 text-center">Detalhes</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50">
                                    {historico.map((item) => {
                                        const isExpanded = expandedRow === item.id;
                                        const detalhes = parseDetalhes(item.detalhes_json);

                                        return (
                                            <React.Fragment key={item.id}>
                                                <tr className="hover:bg-slate-50/50 transition-colors">
                                                    <td className="px-6 py-4">
                                                        <p className="font-bold text-gray-700">{formatDateTime(item.data_hora)}</p>
                                                    </td>
                                                    <td className="px-4 py-4">
                                                        <div className="flex items-center gap-2">
                                                            <span className="w-6 h-6 bg-sabesp-blue/10 text-sabesp-blue rounded-full flex items-center justify-center font-black text-[10px]">
                                                                {(item.usuario || 'A')[0].toUpperCase()}
                                                            </span>
                                                            <span className="font-semibold text-gray-700">{item.usuario || '-'}</span>
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-4">
                                                        <span className="font-mono text-gray-500 bg-gray-50 px-2 py-1 rounded-lg border border-gray-100 truncate max-w-[180px] block" title={item.arquivo_nome}>
                                                            📄 {item.arquivo_nome || '-'}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-4 text-center">
                                                        <span className="font-black text-gray-600">{item.total_etes ?? '-'}</span>
                                                    </td>
                                                    <td className="px-4 py-4 text-center">
                                                        <span className="font-black text-emerald-600">{item.total_atualizados ?? '-'}</span>
                                                    </td>
                                                    <td className="px-4 py-4 text-center">
                                                        <span className="font-black text-amber-500">{item.total_ignorados ?? '-'}</span>
                                                    </td>
                                                    <td className="px-4 py-4 text-center">
                                                        <StatusBadge updated={item.total_atualizados} ignorados={item.total_ignorados} />
                                                    </td>
                                                    <td className="px-4 py-4 text-center">
                                                        {detalhes.length > 0 && (
                                                            <button
                                                                onClick={() => setExpandedRow(isExpanded ? null : item.id)}
                                                                className="text-sabesp-blue hover:underline font-bold text-[10px] uppercase tracking-wider"
                                                            >
                                                                {isExpanded ? '▲ Fechar' : '▼ Ver ETEs'}
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>

                                                {/* Linha expandível com detalhes por ETE */}
                                                {isExpanded && (
                                                    <tr>
                                                        <td colSpan="8" className="px-6 pb-4 pt-0">
                                                            <div className="bg-slate-50 rounded-2xl border border-slate-100 p-4 animate-fadeIn">
                                                                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3">Resultado por ETE</p>
                                                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                                                                    {detalhes.map((d, i) => (
                                                                        <div key={i} className={`text-[10px] px-3 py-2 rounded-xl border border-gray-100 font-bold flex items-center gap-2
                                                                            ${d.status === 'atualizada'
                                                                                ? 'bg-emerald-50 border-emerald-100 text-emerald-700'
                                                                                : 'bg-amber-50 border-amber-100 text-amber-700'
                                                                            }`}>
                                                                            <span>{d.status === 'atualizada' ? '✅' : '⚠️'}</span>
                                                                            {/* ✅ CORREÇÃO CIRÚRGICA: Lê a chave d.nome que vem do banco */}
                                                                            <span className="truncate" title={d.nome}>{d.nome}</span>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

            </div>
        </div>
    );
}

export default AdminPanel;
